import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import SeoMeta from '@/components/feature/SeoMeta';
import { videos, categories, hotTags, siteInfo } from '@/mocks/videos';

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [visibleCount, setVisibleCount] = useState(8);

  const filteredVideos = activeCategory === 'all'
    ? videos
    : videos.filter((v) => v.category === activeCategory);

  const displayedVideos = filteredVideos.slice(0, visibleCount);
  const hasMore = visibleCount < filteredVideos.length;
  const hotVideos = [...videos].sort((a, b) => b.views - a.views).slice(0, 6);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + 8);
  }, []);

  const handleCategoryChange = useCallback((slug: string) => {
    setActiveCategory(slug);
    setVisibleCount(8);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  }, []);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '51黑料',
        url: 'https://www.51heiliao.com',
        description: siteInfo.description,
        foundingDate: siteInfo.founded,
        contactPoint: {
          '@type': 'ContactPoint',
          email: siteInfo.email,
          contactType: 'customer service',
        },
      },
      {
        '@type': 'WebSite',
        name: '51黑料',
        url: 'https://www.51heiliao.com',
        description: siteInfo.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://www.51heiliao.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '51黑料 - 在线观看与视频 | 51黑料网',
        description: '51黑料视频在线盛宴，提供黑料视频、吃瓜视频等高清内容。',
        url: 'https://www.51heiliao.com',
        isPartOf: { '@type': 'WebSite', name: '51黑料' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '51黑料是什么？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '51黑料是一个专注于黑料视频和吃瓜视频的聚合平台，提供娱乐圈、网红圈、直播圈等多个领域的精彩视频内容，支持在线高清流畅播放。',
            },
          },
          {
            '@type': 'Question',
            name: '51黑料有哪些视频分类？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '51黑料涵盖吃瓜视频、黑料视频、一区、二区、直播、影视等多个分类，满足不同用户的观看需求。',
            },
          },
          {
            '@type': 'Question',
            name: '如何在51黑料观看视频？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '用户可以直接在51黑料网站首页选择感兴趣的视频分类，点击视频卡片即可进入播放页面，所有视频均支持高清流畅在线观看。',
            },
          },
          {
            '@type': 'Question',
            name: '51黑料视频更新频率如何？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '51黑料每日更新海量视频内容，确保用户能够第一时间获取最新的黑料视频和吃瓜视频资源。',
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <SeoMeta
        title="51黑料 - 51黑料在线观看与51黑料视频 | 51黑料网在线观看"
        description="51黑料📢视频在线盛宴，整合51黑料在线观看、51黑料网在线观看及一区二区完整资源，提供黑料视频、吃瓜视频等高清内容，直播与影视一应俱全，流畅播放不卡顿。"
        keywords="51黑料在线观看,51黑料在线,51黑料网在线观看,51黑料视频,51黑料在线看,51黑料在线视频,51黑料吃瓜视频,黑料视频观看,51黑料网视频,51黑料一区二区在线"
        canonical="https://www.51heiliao.com/"
        schema={homeSchema}
      />

      <Navbar />

      {/* Hero Section */}
      <header className="relative w-full min-h-[520px] md:min-h-[600px] flex items-center">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20entertainment%20studio%20with%20dramatic%20warm%20red%20and%20amber%20lighting%2C%20professional%20broadcast%20setup%2C%20multiple%20video%20screens%2C%20cinematic%20atmosphere%2C%20dark%20background%20with%20vibrant%20accent%20lights%2C%20high-end%20production%20environment&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
          alt="51黑料在线观看"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="51黑料在线观看"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
        <div className="relative z-10 w-full px-4 md:px-6 mx-auto max-w-7xl text-center">
          <p className="text-accent-400 text-sm md:text-base font-medium tracking-wide mb-3 animate-[fadeInUp_0.6s_ease-out]">
            📢 最新最全的黑料视频聚合平台
          </p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white font-heading leading-tight mb-5 animate-[fadeInUp_0.7s_ease-out]">
            <strong>51黑料在线观看</strong>
            <br />
            <span className="text-accent-400">吃瓜视频 · 一区二区 · 高清流畅</span>
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 animate-[fadeInUp_0.8s_ease-out]">
            整合51黑料网在线观看完整资源，提供黑料视频、吃瓜视频等高清内容，📌直播与影视一应俱全
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-[fadeInUp_0.9s_ease-out]">
            <Link
              to="/category/chigua"
              className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              立即观看 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-medium text-sm rounded-lg backdrop-blur-sm transition-all duration-200 whitespace-nowrap cursor-pointer border border-white/20"
            >
              了解更多
            </Link>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <section className="bg-background-50 border-b border-background-200/70">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-5">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                {siteInfo.stats.videos.toLocaleString()}+
              </div>
              <p className="text-xs md:text-sm text-foreground-600 mt-1">海量视频资源</p>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                {siteInfo.stats.users.toLocaleString()}+
              </div>
              <p className="text-xs md:text-sm text-foreground-600 mt-1">注册用户</p>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                {siteInfo.stats.dailyViews.toLocaleString()}+
              </div>
              <p className="text-xs md:text-sm text-foreground-600 mt-1">日均播放量</p>
            </div>
          </div>
        </div>
      </section>

      <main className="w-full max-w-7xl mx-auto px-4 md:px-6">
        {/* Category Filter */}
        <nav className="mt-10 mb-6" aria-label="视频分类">
          <div className="flex items-center flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                {cat.slug === 'chigua' && <i className="ri-emotion-line mr-1"></i>}
                {cat.slug === 'heiliaovideo' && <i className="ri-alert-line mr-1"></i>}
                {cat.slug === 'yiqu' && <i className="ri-vip-crown-line mr-1"></i>}
                {cat.slug === 'erqu' && <i className="ri-star-line mr-1"></i>}
                {cat.slug === 'zhibo' && <i className="ri-live-line mr-1"></i>}
                {cat.slug === 'yingshi' && <i className="ri-film-line mr-1"></i>}
                {cat.name}
              </button>
            ))}
          </div>
        </nav>

        {/* Content Grid + Sidebar */}
        <div className="flex flex-col lg:flex-row gap-8 mt-6">
          {/* Main Video Grid */}
          <section className="flex-1 min-w-0">
            {displayedVideos.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-video-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">暂无相关视频，请选择其他分类</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                  {displayedVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      id={video.id}
                      title={video.title}
                      slug={video.slug}
                      thumbnail={video.thumbnail}
                      category={video.category}
                      categoryName={categories.find((c) => c.slug === video.category)?.name || video.category}
                      views={video.views}
                      date={video.date}
                      duration={video.duration}
                      isHot={video.isHot}
                    />
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="mt-8 text-center">
                    <button
                      onClick={handleLoadMore}
                      className="px-8 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 hover:text-foreground-900 font-medium text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      加载更多 <i className="ri-arrow-down-line ml-1"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0">
            {/* Hot Ranking */}
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 sticky top-24">
              <h3 className="text-base font-semibold text-foreground-900 font-heading flex items-center gap-2 mb-4">
                <i className="ri-fire-line text-accent-500"></i>
                热门排行
              </h3>
              <ul className="space-y-3">
                {hotVideos.map((video, idx) => (
                  <li key={video.id}>
                    <Link
                      to={`/article/${video.id}`}
                      className="flex items-start gap-3 group cursor-pointer"
                    >
                      <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-xs font-bold ${
                        idx < 3
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </p>
                        <span className="text-xs text-foreground-500 mt-1 block">
                          {formatViews(video.views)} 次播放
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Hot Tags */}
              <div className="mt-6 pt-5 border-t border-background-200/70">
                <h4 className="text-sm font-semibold text-foreground-900 mb-3">热门标签</h4>
                <div className="flex flex-wrap gap-2">
                  {hotTags.map((tag) => (
                    <span
                      key={tag.name}
                      className="px-3 py-1 text-xs rounded-md bg-background-100 text-foreground-700 whitespace-nowrap cursor-pointer hover:bg-primary-500 hover:text-background-50 transition-all duration-200"
                    >
                      {tag.name}
                      <span className="ml-1 text-foreground-400">{tag.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* FAQ Section */}
        <section className="mt-16 mb-14">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                { q: '51黑料是什么？', a: '51黑料是一个专注于黑料视频和吃瓜视频的聚合平台，提供娱乐圈、网红圈、直播圈等多个领域的精彩视频内容，支持在线高清流畅播放。' },
                { q: '51黑料有哪些视频分类？', a: '51黑料涵盖吃瓜视频、黑料视频、一区、二区、直播、影视等多个分类，满足不同用户的观看需求。51黑料在线观看提供一站式的视频浏览体验。' },
                { q: '如何在51黑料观看视频？', a: '用户可以直接在51黑料网站首页选择感兴趣的视频分类，点击视频卡片即可进入播放页面。所有51黑料视频均支持高清流畅在线观看，无需下载任何软件。' },
                { q: '51黑料视频更新频率如何？', a: '51黑料每日更新海量视频内容，确保用户能够第一时间获取最新的51黑料在线视频和吃瓜视频资源。我们的编辑团队全天候监控网络热点。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors list-none">
                    <span><strong>{faq.q}</strong></span>
                    <i className="ri-arrow-down-s-line text-lg transition-transform duration-200 group-open:rotate-180"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-700 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Scroll to Top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg hover:bg-primary-600 transition-all duration-200 z-40 cursor-pointer animate-[fadeIn_0.3s_ease-out]"
          aria-label="返回顶部"
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      )}

      <Footer />
    </>
  );
}